import { useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAppContext, useSelectedAccount, useSelectedBroker } from '../contexts/hooks';
import { useAccountData } from '../hooks/useAccountData';
import StepProgress from '../components/StepProgress';
import { trackEvent } from '../lib/analytics';
import { Card, CardContent } from '@/components/ui/card';
import { OdsBadge } from '@/components/orderly/OdsBadge';

export default function AccountAction() {
  const { state, dispatch } = useAppContext();
  const account = useSelectedAccount();
  const broker = useSelectedBroker();
  const { refetch } = useAccountData();
  const trackedAccountKeyRef = useRef<string | null>(null);

  useEffect(() => {
    trackEvent('component_viewed', {
      component: 'AccountAction',
      page: 'account_action',
      broker_id: broker?.id ?? undefined,
      account_id: account?.accountId ?? undefined,
      account_type: account?.type ?? undefined,
    });
  }, [broker?.id, account?.accountId, account?.type]);

  useEffect(() => {
    if (!account || !broker) return;
    const key = `${broker.id}:${account.accountId}`;
    if (trackedAccountKeyRef.current === key) return;

    trackEvent('account_viewed', {
      account_id: account.accountId,
      account_type: account.type,
      broker_id: broker.id,
    });
    trackedAccountKeyRef.current = key;
  }, [account, broker]);

  if (!account || !broker) return null;

  const allDone = account.positions.length === 0
    && account.pendingOrders.length === 0
    && account.unsettledPnl === 0
    && account.holdings.every((h) => h.holding === 0);

  return (
    <div className="space-y-6">
      <button
        type="button"
        className="inline-flex items-center gap-2 text-sm font-medium text-[var(--fg-secondary)] transition-colors hover:text-[var(--fg-primary)]"
        onClick={() => dispatch({ type: 'BACK_TO_OVERVIEW' })}
      >
        <ArrowLeft className="h-4 w-4" />
        Back to accounts
      </button>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display text-2xl text-[var(--fg-base)]">{account.name}</h2>
          <OdsBadge type={account.type === 'main' ? 'brand' : 'subtle'}>
            {account.type === 'main' ? 'Main' : 'Sub'}
          </OdsBadge>
        </div>
        <span className="break-all font-mono text-xs text-[var(--fg-secondary)]">{account.accountId}</span>
      </div>

      <Card className="border-[var(--border-subtle)] bg-[var(--bg-surface-primary)] py-0 shadow-[var(--shadow-popout)]">
        <CardContent className="p-4 sm:p-6">
          <StepProgress onStepComplete={refetch} />
        </CardContent>
      </Card>

      {allDone && (
        <div className="rounded-xl border border-[var(--border-positive)] bg-[var(--bg-positive-subtle)] p-5 text-center text-base font-semibold text-[var(--fg-positive)]">
          {state.steps.withdraw === 'completed'
            ? 'All exit requests submitted. Refresh balances to confirm completion.'
            : 'All done! This account has been fully offboarded.'}
        </div>
      )}
    </div>
  );
}
